/**
 * @module URI
 * @since 1.4.0
 */

import { select } from '../../object';
import { analyze } from '../__analyze';
import { toQueryString } from '../toQueryString';
import { queryStringToObject } from '../queryStringToObject';

/**
 * Key to use for stored calculated values of URI struct
 * @type {Symbol}
 */
const store = typeof Symbol === 'function' ? Symbol() : '_store';

/**
 * Map param types from symbols to names
 * @private
 * @type {Object}
 */
const QUERY_TYPE_DICTIONARY = {
    '#!': 'hashParams',
    '#': 'hashParams',
    '?': 'queryParams'
};

/**
 * Assign key value pair to Self store object, and recreate a frozen store
 * @private
 * @param  {URI}     self context
 * @param  {String}  prop Property to set
 * @param  {Any}     getter
 * @param  {Boolean} [force=true] should set even if the property exists
 * @return {URI}
 */
function _assign(self, prop, getter, force = false) {
    if (force || !(prop in self.store)) {
        self[store] = Object.freeze(
            Object.assign(
                {},
                self.store,
                { [prop]: getter() }
            )
        );
    }

    return self;
}

/**
 * rebuild href string according to new parameters
 * @private
 * @param  {URI} self context
 * @param  {Strong[]} props Properties to use in the rebuilt solution (skip re calculation)
 * @return {URI}
 */
function _rebuild(self, ...props) {
    self[store] = Object.freeze(
        Object.assign(...[{
            href: [
                self.protocol || self.host ? `${self.protocol}//` : '',
                self.host,
                self.pathname,
                (self.hasQueryParams() ? `?${toQueryString(self.queryParams)}` : ''),
                (self.hasHashParams() ? `#${toQueryString(self.hashParams)}` : '')
            ].join('')
        }]
            .concat(
                props.map(
                    (prop) => ({ [prop]: self.store[prop] })
                )
            )
        ));

    return self;
}

/**
 * @class URI
 * @classdesc URI parser with params manipulation. An extension of the native URL class.
 * @property {String} href The URL's "href" property.
 * @property {String} path The URL's "path" property.
 * @property {String} pathname The URL's "pathname" property.
 * @property {String} host The URL's "host" property.
 * @property {String} top The URL's "top" property.
 * @property {String} protocol The URL's "protocol" property.
 * @property {String} query The URL's query string.
 * @property {Object} queryParams The URL's query parameters.
 * @property {String} hash The URL's hash string.
 * @property {Object} hashParams The URL's hash parameters.
 * @property {Object} params All of the URL's parameters.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/URL/URL
 *
 * @example
 * const uri = new URI('website.com/page?param1=1');
 * uri.host;     // "website.com"
 * uri.pathname; // "/page"
 * uri.params;   // {param1: "1"}
 */
export class URI {
    /**
     * @param {String} href Full URI string
     */
    constructor(href) {
        this[store] = Object.freeze({ href });
    }

    /**
     * A pointer to the internal store property.
     * @return {Object}
     */
    get store() {
        return this[store];
    }

    /**
     * The URL's "href" property.
     * @type {String}
     * @readonly
     */
    get href() {
        return this.store.href;
    }

    /**
     * The URL's "path" property.
     * @type {String}
     * @readonly
     */
    get path() {
        return _assign(
            this,
            'path',
            () => analyze.path(this.href)
        ).store.path;
    }

    /**
     * The URL's "pathname" property.
     * @type {String}
     * @readonly
     */
    get pathname() {
        this.path; // Should pre calculate
        this.host; // Should pre calculate

        return _assign(
            this,
            'pathname',
            () => analyze.pathname(this.path, this.host)
        ).store.pathname;
    }

    /**
     * The URL's "host" property.
     * @type {String}
     * @readonly
     */
    get host() {
        this.path; // Should pre calculate

        return _assign(
            this,
            'host',
            () => analyze.host(this.path)
        ).store.host;
    }

    set host(host) {
        this.pathname; // Should pre calculate

        _rebuild(
            _assign(
                this,
                'host',
                () => host,
                true
            ),
            'host'
        );
    }

    /**
     * The URL's "top" property.
     * @type {String}
     * @readonly
     */
    get top() {
        this.host; // Should pre calculate

        return _assign(
            this,
            'top',
            () => analyze.top(this.host)
        ).store.top;
    }

    /**
     * The URL's "protocol" property.
     * @type {String}
     * @readonly
     */
    get protocol() {
        this.path; // Should pre calculate

        return _assign(
            this,
            'protocol',
            () => analyze.protocol(this.path)
        ).store.protocol;
    }

    /**
     * The URL's query string.
     * @type {String}
     * @readonly
     */
    get query() {
        return _assign(
            this,
            'query',
            () => analyze.query(this.href)
        ).store.query;
    }

    /**
     * The URL's query parameters.
     * @type {Object}
     */
    get queryParams() {
        this.query; // Should pre calculate

        return _assign(
            this,
            'queryParams',
            () => Object.freeze(queryStringToObject(this.query))
        ).store.queryParams;
    }

    set queryParams(obj) {
        this.queryParams; // Should pre calculate

        _rebuild(
            _assign(
                this,
                'queryParams',
                () => Object.assign({}, this.queryParams, obj),
                true
            ),
            'queryParams'
        );
    }

    /**
     * The URL's hash string.
     * @type {String}
     * @readonly
     */
    get hash() {
        return _assign(
            this,
            'hash',
            () => analyze.hash(this.href)
        ).store.hash;
    }

    /**
     * The URL's hash parameters property.
     * @type {Object}
     */
    get hashParams() {
        this.hash; // Should pre calculate

        return _assign(
            this,
            'hashParams',
            () => Object.freeze(queryStringToObject(this.hash))
        ).store.hashParams;
    }

    set hashParams(obj) {
        this.hashParams; // Should pre calculate

        _rebuild(
            _assign(
                this,
                'hashParams',
                () => Object.assign({}, this.hashParams, obj),
                true
            ),
            'hashParams'
        );
    }

    /**
     * All of the URL's parameters.
     * @type {Object}
     */
    get params() {
        this.queryParams; // Should pre calculate
        this.hashParams; // Should pre calculate

        return Object.assign({}, this.queryParams, this.hashParams);
    }

    set params(obj) {
        this.params; // Should pre calculate

        const method = !this.hasQueryParams() && this.hasHashParams() ? 'hash' : 'query';

        this[`${method}Params`] = obj;
    }

    /**
     * Override the native `toString` function.
     * @return {String} The full href string
     *
     * @example
     *
     * const uri = new URI('website.com/page?param1=1');
     * uri.toString(); // 'website.com/page?param1=1&param2=2'
     * '' + uri; // 'website.com/page?param1=1&param2=2'
     * `${uri}`; // 'website.com/page?param1=1&param2=2'
     */
    toString() {
        return this.href;
    }

    /**
     * Checks whether the URL has any query parameters or not.
     * @return {Boolean}
     */
    hasQueryParams() {
        return Object.keys(this.queryParams).length > 0;
    }

    /**
     * Checks whether the URL has any hash parameters or not.
     * @return {Boolean}
     */
    hasHashParams() {
        return Object.keys(this.hashParams).length > 0;
    }

    /**
     * Add parameters to query or hash.
     * @param {Object} obj Key-value pairs.
     * @param {String} [mod='?'] Which param set to address.
     *
     * @example
     *
     * uri.addParams({key: 'value'});
     * uri.addParams({page: 'gig'}, '#');
     */
    addParams(obj, mod) {
        this.params; // Should pre calculate

        const method = QUERY_TYPE_DICTIONARY[mod] || 'params';

        this[method] = obj;

        return this;
    }

    /**
     * Checks whether the URL has any parameters at all.
     * @return {Boolean} Has any query or hash params
     */
    hasParams() {
        return Object.keys(this.params).length > 0;
    }

    /**
     * Remove parameters from query and hash.
     * @param  {String[]} keys Parameters to remove
     * @return {Self}
     *
     * @example uri.removeParams('param_a', 'param_b');
     */
    removeParams(...keys) {
        this.params; // Should pre calculate

        if (!keys.length) {

            // Remove all
            this[store] = Object.freeze(
                Object.assign(...[
                    {},
                    this.store,
                    {
                        hashParams: {},
                        queryParams: {}
                    }
                ])
            );
        } else {
            this[store] = Object.freeze(
                Object.assign(...[
                    {},
                    this.store
                ].concat(
                    ['hashParams', 'queryParams'].map((prop) => ({
                        [prop]: select(this[prop], (key) => !~keys.indexOf(key))
                    }))
                ))
            );
        }

        return _rebuild(this, 'queryParams', 'hashParams');
    }
}
